import { useEffect, useRef } from 'react';

import { useCatalogContext } from '../../state/Catalog';
import { useCatalogRegistryTranslations } from '../../Catalog.i18n';
import { useCatalogRegistryTelemetry } from '../../Catalog.telemetry';

import { useGetPdpDataByProductIdLazyQuery, useGetPdpDataByProductIdQuery } from '../../api/queries/getPdpDataByProductId';
import { withWindow } from '@shared/utils/withWindow';
import { useShopStorefrontContext } from '../../components/ShopStorefrontProvider';
import { CatalogStorefront } from '@graphql/generated';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useRegistryShopping } from '@apps/registry/admin/RegistryShopping.provider';

export const useCatalogProductDetails = ({ catalogItemId }: { catalogItemId: string }) => {
  const { catalogProductDetailsQueryError } = useCatalogRegistryTelemetry();

  const { getAddProductTranslations } = useCatalogRegistryTranslations();
  const { notificationText } = getAddProductTranslations();
  const { changeStorefront, isBabyStorefront } = useShopStorefrontContext();
  const { isEnabled: isShoppingEnabled } = useRegistryShopping();
  const { isAdminView, addCatalogItemToRegistry, addCatalogItemLoading, notificationVisible, setNotificationVisible, selectedCategory } = useCatalogContext();

  const { product, error, loading } = useGetPdpDataByProductIdQuery({
    catalogItemId,
    options: {
      batchMode: 'fast',
      variables: {
        id: catalogItemId,
        withPdpSections: true,
        withProductRating: true,
        withInventory: isShoppingEnabled
      },
      onCompleted(data) {
        const productSubcategory = data.productCatalogItemById?.subCategories.at(-1);
        // 1. Only handle this scenario in public shop where users can toggle storefronts
        // 2. The following only happens when a user is on a PDP page and refreshes the browser tab,
        //    losing track of the selected category
        if (!isAdminView && productSubcategory && !selectedCategory) {
          const shouldBeViewingBabyStorefront = productSubcategory.publicUrlPath.startsWith('/shop/baby/catalog');
          if (shouldBeViewingBabyStorefront && !isBabyStorefront) {
            changeStorefront(CatalogStorefront.baby);
          }
        }
      }
    }
  });

  const { sendRequest: getPdpDataByProductId, product: productDetails, loading: productDetailsLoading } = useGetPdpDataByProductIdLazyQuery({
    catalogItemId: catalogItemId,
    options: {
      batchMode: 'fast'
    }
  });

  useEffect(() => {
    if (error && catalogItemId) {
      catalogProductDetailsQueryError({ catalogItemId, error });
    }
  }, [catalogProductDetailsQueryError, catalogItemId, error]);

  const hasScrolledToTop = useRef(false);

  if (!hasScrolledToTop.current) {
    // only scroll to the top once, doing this outside of a use effect because we want it to happen instantaneously
    hasScrolledToTop.current = true;
    withWindow(window => {
      // @ts-ignore
      window.scrollTo({ top: 0, behavior: 'instant' });
    });
  }

  const { value: shopProductConfirmationToastV2Enabled } = useFeatureValue('shopProductConfirmationToastV2');

  return {
    addCatalogItemToRegistry,
    addCatalogItemLoading,
    hasError: !!error,
    notificationText,
    notificationVisible,
    productLoading: productDetailsLoading || loading,
    product: productDetails || product || null,
    setNotificationVisible,
    error,
    getPdpDataByProductId,
    shopProductConfirmationToastV2Enabled: shopProductConfirmationToastV2Enabled === 'on'
  };
};
